import { classNames } from 'primereact/utils';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import useDialogContext from '../../../../../../../../../../hooks/dialog/useDialogContext';
import { MAX_PROCESS_PER_CALCULATION } from '../../../../../../../../../../shared/constants/products.constant';
import { IProcessDetails } from '../../../../../../../../../../shared/interfaces/IFootprint';
import AddProcessButton from './AddProcessButton';
import {
  checkGeneralInformationValidity,
  GENERAL_INFORMATION_SECTION_FIELD_NAMES,
} from './helpers';
import ProcessCard from './ProcessCard';

const ManufacturingProcessSection = () => {
  const { control, trigger, getFieldState, getValues } = useFormContext();
  const { openDialog } = useDialogContext();
  const { t } = useTranslation();

  // Listen for General Information Section fields
  const generalInformationValues = useWatch({
    name: GENERAL_INFORMATION_SECTION_FIELD_NAMES,
    control,
  });

  // Listen for processes
  const processes = useWatch({
    name: 'processes',
    control,
  }) as IProcessDetails[];

  const { fields, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'processes', // unique name for your Field Array
  });

  const [isDisabled, setIsDisabled] = useState(true);

  // State variable to show add process button
  const showAddProcessButton = useMemo(() => {
    if (processes.length >= MAX_PROCESS_PER_CALCULATION) return false;

    return true;
  }, [processes]);

  useEffect(() => {
    // Check General Information Section fields, if they are not valid, disable this section
    checkGeneralInformationValidity(generalInformationValues, trigger, setIsDisabled);
  }, [generalInformationValues, trigger, getFieldState]);

  const onDeleteProcess = (index: number, showConfirmationDialog: boolean) => {
    // Remove the process at the specified index
    if (index < 0) {
      return;
    }

    if (showConfirmationDialog) {
      openDialog('confirmation-dialog', {
        title: t('deleteProcessDialog.title', { name: processes[index].name }),
        message: t('deleteProcessDialog.message'),
        confirmButtonText: t('deleteProcessDialog.buttons.delete'),
        onConfirm: () => {
          remove(index);
        },
      });
    } else {
      remove(index);
    }
  };

  return (
    <div
      className={classNames('flex flex-column w-full gap-4', {
        'p-disabled': isDisabled,
      })}
    >
      {/* Stack process cards */}
      {fields.map((field, index) => {
        const process = getValues(`processes[${index}]`);

        return (
          <ProcessCard
            key={field.id}
            index={index}
            process={process}
            onDelete={(showConfirmationDialog: boolean) => {
              onDeleteProcess(index, showConfirmationDialog);
            }}
          />
        );
      })}

      {!isDisabled && showAddProcessButton && <AddProcessButton />}
    </div>
  );
};

export default ManufacturingProcessSection;
